import { useState } from 'react'

import { Divider, Loader, Modal, TextInput } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'

import { getOperationName } from '@apollo/client/utilities'
import { useMutation, useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledBadge from 'src/components/DataDisplay/StyledBadge/StyledBadge'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { MERGE_APPLICANT } from 'src/graphql/applicantMergeJobs.graphql'
import {
  SEARCH_APPLICANTS,
  GET_APPLICANT_BY_ID,
  GET_APPLICANTS,
} from 'src/graphql/applicants.graphql'
import IconSearch from 'src/icons/IconSearch'
import {
  buildFullName,
  calculateStageBadgeColor,
  formatSnakeValue,
} from 'src/lib/formatters'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'

interface MergeApplicantModalProps {
  opened: boolean
  close: () => void
  applicantId: string
  applicantName: string
  onMergeInitiated?: () => void
}

const MergeApplicantModal = ({
  opened,
  close,
  applicantId,
  applicantName,
  onMergeInitiated,
}: MergeApplicantModalProps) => {
  const [step, setStep] = useState<'search' | 'preview'>('search')
  const [searchText, setSearchText] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchText, 300)
  const [selectedApplicant, setSelectedApplicant] = useState(null)

  const { data: searchData, loading: searchLoading } = useQuery(
    SEARCH_APPLICANTS,
    {
      variables: {
        pageInfo: { limit: 10, offset: 0 },
        filters: { searchText: debouncedSearch },
      },
      skip: !debouncedSearch || debouncedSearch.length < 2,
    }
  )

  const [mergeApplicant, { loading: merging }] = useMutation(MERGE_APPLICANT, {
    refetchQueries: [
      { query: GET_APPLICANT_BY_ID, variables: { id: applicantId } },
      getOperationName(GET_APPLICANTS),
    ],
    onCompleted: () => {
      toast(
        "Merge initiated. You'll receive an email when complete.",
        'success'
      )
      handleClose()
      onMergeInitiated?.()
    },
    onError: (error) => {
      toast(error.message || 'Failed to initiate merge', 'error')
    },
  })

  const handleClose = () => {
    setStep('search')
    setSearchText('')
    setSelectedApplicant(null)
    close()
  }

  const handleSelectApplicant = (applicant) => {
    setSelectedApplicant(applicant)
    setStep('preview')
  }

  const handleConfirmMerge = () => {
    mergeApplicant({
      variables: {
        duplicateApplicantId: applicantId,
        primaryApplicantId: selectedApplicant.applicantId,
      },
    })
  }

  const searchResults =
    searchData?.applicants?.items?.filter(
      (a) => a.applicantId !== applicantId
    ) || []

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size="lg"
      withCloseButton={false}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-base font-semibold text-doubleNickel-gray-900">
            Merge Applicant
          </div>
          {step === 'search' && (
            <div className="text-sm text-doubleNickel-gray-600">
              Search for the primary applicant to merge{' '}
              <span className="font-medium">{applicantName}</span> into. This
              applicant's data will be copied to the primary, and this record
              will be archived.
            </div>
          )}
          {step === 'preview' && (
            <div className="text-sm text-doubleNickel-gray-600">
              <span className="font-medium">{applicantName}</span>'s data will
              be merged into{' '}
              <span className="font-medium">
                {buildFullName(selectedApplicant)}
              </span>
              . Existing information on the primary applicant will not be
              overwritten.
            </div>
          )}
        </div>

        <Divider />

        {step === 'search' && (
          <>
            <TextInput
              placeholder="Search by name, phone, or email..."
              leftSection={
                <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-400" />
              }
              value={searchText}
              onChange={(e) => setSearchText(e.currentTarget.value)}
              autoFocus
            />

            <div className="max-h-64 overflow-y-auto">
              {searchLoading && (
                <div className="flex justify-center py-4">
                  <Loader size="sm" />
                </div>
              )}

              {!searchLoading &&
                debouncedSearch &&
                searchResults.length === 0 && (
                  <div className="py-4 text-center text-sm text-doubleNickel-gray-500">
                    No applicants found
                  </div>
                )}

              {searchResults.map((applicant) => (
                <div
                  key={applicant.applicantId}
                  className="flex cursor-pointer flex-row items-center gap-1 px-4 py-3 hover:bg-doubleNickel-gray-100"
                  onClick={() => handleSelectApplicant(applicant)}
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-row gap-2 text-sm font-medium text-doubleNickel-gray-900">
                      {buildFullName(applicant)}
                      <StyledBadge
                        color={calculateStageBadgeColor(applicant.applicantStage)}
                        textTransform="capitalize"
                      >
                        {applicant.applicantStage === 'PROSPECT'
                          ? formatSnakeValue(applicant.hiringStage)
                          : formatSnakeValue(applicant.applicantStage)}
                      </StyledBadge>
                    </div>
                    <div className="text-xs text-doubleNickel-gray-500">
                      {applicant.jobListing?.title}
                      {applicant.phone && (
                        <> · {formatInternationalPhoneNumber(applicant.phone)}</>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Divider />
            <div className="flex justify-end">
              <DNButton variant="outline" onClick={handleClose}>
                Cancel
              </DNButton>
            </div>
          </>
        )}

        {step === 'preview' && (
          <>
            <div className="rounded-lg border border-doubleNickel-warning-200 bg-doubleNickel-warning-25 p-3 text-sm text-doubleNickel-warning-700">
              Once confirmed, this applicant's record will be archived and
              their information will be added to the primary applicant. This
              may take a few moments — we'll send an email to all assigned
              employees and to you when it's done.
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-doubleNickel-gray-200 p-3">
                <div className="mb-1 text-xs font-medium uppercase text-doubleNickel-gray-500">
                  This applicant (will be archived)
                </div>
                <div className="text-sm font-semibold text-doubleNickel-gray-900">
                  {applicantName}
                </div>
              </div>
              <div className="rounded-lg border border-doubleNickel-brand-200 bg-doubleNickel-brand-25 p-3">
                <div className="mb-1 text-xs font-medium uppercase text-doubleNickel-brand-600">
                  Primary applicant (will receive data)
                </div>
                <div className="text-sm font-semibold text-doubleNickel-gray-900">
                  {buildFullName(selectedApplicant)}
                </div>
              </div>
            </div>

            <Divider />
            <div className="flex justify-end gap-3">
              <DNButton
                variant="outline"
                onClick={() => {
                  setStep('search')
                  setSelectedApplicant(null)
                }}
              >
                Back
              </DNButton>
              <DNButton
                variant="primary"
                onClick={handleConfirmMerge}
                loading={merging}
              >
                Confirm Merge
              </DNButton>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

export default MergeApplicantModal
